/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 17/04/2025 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Se agregan servicios de laboratorio de plantillas de ticket
*      3       | 03/06/2026 | Lic. José Luis Aragón Cervantes  | Homologación de plantilla ticket con contrato único por idcaja
* ---------------------------------------------------------------------------------------
*/

import {
  apiParametrosCaja,
  apiSaveParametrosCaja,
  apiListaEquiposSistema,
  apiParametrosTicket,
  apiSaveParametrosTicket,
} from "../../settings/ApiConfig";
import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { isNullOrEmpty } from "../../settings/utils";
import { useRequest } from "../System/RequestContext";
import { useLayout } from "../System/LayoutContext";

export const ParametrosContext = createContext();

const normalizeCaja = (raw = {}) => {
  const trim = (v) => (v ?? "").toString().trim();
  const trimEnd = (v) => (v ?? "").toString().trimEnd();
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  return {
    ...raw,
    // ids / textos
    idcaja: trim(raw.idcaja ?? raw.idCaja ?? raw.id ?? ""),
    caja: trim(raw.caja ?? ""),
    dominio: trim(raw.dominio ?? ""),

    // folios (OJO: con N)
    folioventa: num(raw.folioventa),
    foliopreventa: num(raw.foliopreventa),
    foliocotizacion: num(raw.foliocotizacion),
    foliodevolucion: num(raw.foliodevolucion),
    folioapartado: num(raw.folioapartado),
    foliocortecaja: num(raw.foliocortecaja),

    // ticket
    encabezadoticket: raw.encabezadoticket ?? raw.sEncabezadoTicket ?? "",
    sEncabezadoTicket: (raw.sEncabezadoTicket ?? "") || (raw.encabezadoticket ?? ""),
    pieticket: raw.pieticket ?? raw.sPieDeTicket ?? "",
    sPieDeTicket: (raw.sPieDeTicket ?? "") || (raw.pieticket ?? ""),
    logo: raw.logo ?? raw.Logo ?? "",

    // impresoras
    nombreimpresoraventas: trim(raw.nombreimpresoraventas),
    nombreimpresorapreventas: trim(raw.nombreimpresorapreventas),
    nombreimpresoracotizaciones: trim(raw.nombreimpresoracotizaciones),
    nombreimpresoraapartados: trim(raw.nombreimpresoraapartados),
    nombreimpresorareportes: trim(raw.nombreimpresorareportes),
    nombreimpresorabanner: trim(raw.nombreimpresorabanner),
    nombreimpresoraetiqueta: trim(raw.nombreimpresoraetiqueta),
    nombreimpresoracodigobarras: trim(raw.nombreimpresoracodigobarras),

    // WhatsApp (se emiten ambos nombres para compatibilidad con servidor y frontend)
    idphonewhatsapp: trim(raw.idphonewhatsapp ?? raw.sIdPhoneWhatsApp ?? ""),
    sIdPhoneWhatsApp: trim((raw.sIdPhoneWhatsApp ?? "") || (raw.idphonewhatsapp ?? "")),
    tokenaccesowhatsapp: trim(raw.tokenaccesowhatsapp ?? raw.sTokenAccesoWhatsApp ?? ""),
    sTokenAccesoWhatsApp: trim((raw.sTokenAccesoWhatsApp ?? "") || (raw.tokenaccesowhatsapp ?? "")),
  };
};

const normalizeTicket = (raw = {}) => {
  const trim = (v) => (v ?? "").toString().trim();
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  return {
    ...raw,
    id: num(raw.id),
    idcaja: trim(raw.idcaja),
    caja: trim(raw.caja ?? ""),
    tipoticket: trim(raw.tipoticket ?? "VENTA").toUpperCase() || "VENTA",
    nombreplantilla: trim(raw.nombreplantilla ?? "Ticket ventas definitivo"),
    plantillajson: raw.plantillajson ?? "",
    version: num(raw.version || 1),
    activa: num(raw.activa ?? 1),
  };
};

export const ParametrosProvider = (props) => {
  const [parametros, setParametros] = useState({});
  const [listaEquipos, setListaEquipos] = useState([]);

  const navigation = useNavigate();
  const { handleOpenAlert } = useLayout();
  const { GetRequest, PostRequest } = useRequest();

  const handleClickRegresarMainDashboard = () => {
    navigation("/*");
  };

  const handleobtenerListaEquipos = async () => {
    const response = await GetRequest({
      url: apiListaEquiposSistema,
      loader: "Consultando lista de equipos del sistema...",
    });

    if (response.hasError) {
      handleOpenAlert("No hay datos de equipo en sistema");
      setListaEquipos([]);
      return [];
    }

    setListaEquipos(response?.result ?? []);
    return response?.result ?? [];
  };

  // ✅ NUEVO: Cargar parámetros de una caja/equipo
  const handleObtenerParametrosCaja = async (idCaja) => {
    if (isNullOrEmpty(idCaja)) return null;

    const url = apiParametrosCaja.replace("{idCaja}", idCaja);

    const response = await GetRequest({
      url,
      loader: "Consultando parámetros de caja...",
    });

    if (response.hasError) {
    
      handleOpenAlert("No es posible obtener parámetros: " + response.message);
      return null;
    }


    const norm = normalizeCaja(response?.result ?? {});
    setParametros(norm);
    return norm;
  };

  const handleSaveParametrosCaja = async (dataPC) => {
    if (isNullOrEmpty(dataPC)) return null;

    const payload = normalizeCaja(dataPC);

    const response = await PostRequest({
      url: apiSaveParametrosCaja,
      body: payload,
      loader: "Se está actualizando el registro de parámetros del equipo...",
    });

    if (response.hasError) {
      handleOpenAlert("No es posible guardar los cambios de los parámetros: " + response.message);
      return null;
    }

    const norm = normalizeCaja(response?.result ?? payload);
    setParametros(norm);
    handleOpenAlert("Cambio aplicado correctamente", "alerta");
    return norm;
  };

  const handleObtenerParametrosTicket = async (idcaja, tipoTicket = "VENTA") => {
    const idcajaNormalizada = (idcaja ?? "").toString().trim().toUpperCase();
    const tipoTicketNormalizado = (tipoTicket || "VENTA").toString().trim().toUpperCase();
    if (isNullOrEmpty(idcajaNormalizada)) return null;

    const url = apiParametrosTicket
      .replace("{idCaja}", encodeURIComponent(idcajaNormalizada))
      .replace("{tipoTicket}", encodeURIComponent(tipoTicketNormalizado));

    const response = await GetRequest({
      url,
      loader: "Consultando plantilla de ticket...",
    });

    if (response.hasError) {
      handleOpenAlert("No es posible obtener plantilla de ticket: " + response.message, "error");
      return null;
    }

    return normalizeTicket(response?.result ?? {});
  };

  const handleSaveParametrosTicket = async (ticket) => {
    if (isNullOrEmpty(ticket)) return null;

    const payload = normalizeTicket(ticket);
    const response = await PostRequest({
      url: apiSaveParametrosTicket,
      body: payload,
      loader: "Guardando plantilla de ticket...",
    });

    if (response.hasError) {
      handleOpenAlert("No es posible guardar plantilla de ticket: " + response.message, "error");
      return null;
    }

    handleOpenAlert("Plantilla de ticket guardada correctamente", "alerta");
    return normalizeTicket(response?.result ?? payload);
  };

  return (
    <ParametrosContext.Provider
      value={{
        handleClickRegresarMainDashboard,
        handleobtenerListaEquipos,
        handleObtenerParametrosCaja, // ✅
        listaEquipos,
        handleSaveParametrosCaja,
        handleObtenerParametrosTicket,
        handleSaveParametrosTicket,
        parametros,
        setParametros,
        normalizeCaja, // opcional (útil para UI)
        normalizeTicket,
      }}
    >
      {props.children}
    </ParametrosContext.Provider>
  );
};

export const useParametros = () => useContext(ParametrosContext);
